import {
  IAnalyticsData,
  IElementButton,
  IEmailStarterLayout,
} from '@dataTypes/marketing.type'
import { FEATURE_ROW_DEFAULT_SVG } from './featureRowIcons'

export enum StatusType {
  increase = 'increase',
  decrease = 'decrease',
  neutral = 'neutral',
}

export const initialAnalyticsData: IAnalyticsData = {
  previous: 0,
  current: 0,
  percentage: 0,
  status: StatusType.neutral,
}

export const PLATFORM_OPTIONS = [
  { label: 'Select platform', value: '' },
  { label: 'Email', value: 'Email' },
  { label: 'Instagram', value: 'Instagram' },
  { label: 'Facebook', value: 'Facebook' },
  { label: 'Twitter', value: 'Twitter' },
  { label: 'TikTok', value: 'TikTok' },
  { label: 'LinkedIn', value: 'LinkedIn' },
  { label: 'SMS', value: 'SMS' },
]

export const UPLOAD_TIME_OPTIONS = [
  { label: '8:00 AM', value: '8:00:00' },
  { label: '8:15 AM', value: '8:15:00' },
  { label: '8:30 AM', value: '8:30:00' },
  { label: '8:45 AM', value: '8:45:00' },
  { label: '9:00 AM', value: '9:00:00' },
  { label: '9:15 AM', value: '9:15:00' },
  { label: '9:30 AM', value: '9:30:00' },
  { label: '9:45 AM', value: '9:45:00' },
  { label: '10:00 AM', value: '10:00:00' },
  { label: '10:15 AM', value: '10:15:00' },
  { label: '10:30 AM', value: '10:30:00' },
  { label: '10:45 AM', value: '10:45:00' },
  { label: '11:00 AM', value: '11:00:00' },
  { label: '11:15 AM', value: '11:15:00' },
  { label: '11:30 AM', value: '11:30:00' },
  { label: '11:45 AM', value: '11:45:00' },
  { label: '12:00 PM', value: '12:00:00' },
  { label: '12:15 PM', value: '12:15:00' },
  { label: '12:30 PM', value: '12:30:00' },
  { label: '12:45 PM', value: '12:45:00' },
  { label: '1:00 PM', value: '13:00:00' },
  { label: '1:15 PM', value: '13:15:00' },
  { label: '1:30 PM', value: '13:30:00' },
  { label: '1:45 PM', value: '13:45:00' },
  { label: '2:00 PM', value: '14:00:00' },
  { label: '2:15 PM', value: '14:15:00' },
  { label: '2:30 PM', value: '14:30:00' },
  { label: '2:45 PM', value: '14:45:00' },
  { label: '3:00 PM', value: '15:00:00' },
  { label: '3:15 PM', value: '15:15:00' },
  { label: '3:30 PM', value: '15:30:00' },
  { label: '3:45 PM', value: '15:45:00' },
  { label: '4:00 PM', value: '16:00:00' },
  { label: '4:15 PM', value: '16:15:00' },
  { label: '4:30 PM', value: '16:30:00' },
  { label: '4:45 PM', value: '16:45:00' },
  { label: '5:00 PM', value: '17:00:00' },
  { label: '5:15 PM', value: '17:15:00' },
  { label: '5:30 PM', value: '17:30:00' },
  { label: '5:45 PM', value: '17:45:00' },
  { label: '6:00 PM', value: '18:00:00' },
]

export const TRIGGER_DURATION_OPTIONS = [
  { label: 'Hour(s)', value: 'Hour/s' },
  { label: 'Day(s)', value: 'Day/s' },
  { label: 'Week(s)', value: 'Week/s' },
]

export const RECURRING_INTERVAL_OPTIONS = [
  { label: 'Daily', value: 'Daily' },
  { label: 'Weekly', value: 'Weekly' },
]

export const daysOfWeek = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
export const fullDaysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

export const MONTH_OPTIONS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/**
 * One full-width row in the email body. Used for the Row element and to wrap
 * Button, Image, Logo, and Divider so each block is a predictable stack unit.
 */
export function wrapEmailBlockInRow(innerHtml: string): string {
  return `<div class="e-rte-email-row" data-email-row="true" style="max-width:100%;margin:12px 0;padding:2px 0;">${innerHtml}</div>`
}

/** Insertable blocks for Marketing → Templates (email-style editor). */
export const TEMPLATE_EMAIL_SNIPPETS = {
  layout: `<div class="e-rte-email-layout" data-element="layout" style="max-width:100%;margin:14px 0;border:1px solid #e5e7eb;border-radius:10px;background:#ffffff;overflow:hidden;box-shadow:0 1px 3px rgba(15,23,42,0.06);"><div style="padding:12px 16px 10px;background-color:#f8fafc;border-bottom:1px solid #f1f5f9;"><p style="margin:0;font-size:inherit;font-weight:600;color:#1e293b;letter-spacing:-0.01em;">Section title</p></div><div style="padding:16px 16px 20px;min-height:56px;"><p style="margin:0;font-size:inherit;line-height:1.6;color:#64748b;">Add body copy, bullets, or details here.</p></div></div>`,
  row: wrapEmailBlockInRow(
    `<p style="margin:0;min-height:24px;font-size:inherit;line-height:1.5;color:#64748b;">New text block — type here or use Elements to add buttons, images, and more.</p>`,
  ),
  columns: wrapEmailBlockInRow(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border-spacing:0;border:0;"><tr><td valign="top" width="50%" style="padding:0 8px 0 0;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Column one</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Add your first paragraph here.</p></td><td valign="top" width="50%" style="padding:0 0 0 8px;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Column two</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Add your second paragraph here.</p></td></tr></table>`,
  ),
  button: `<p style="text-align:center;margin:12px 0;"><a class="e-rte-email-btn" data-mt-cta-slot="1" href="https://example.com" style="display:inline-block;min-width:200px;box-sizing:border-box;text-align:center;padding:12px 28px;background:var(--mt-email-cta-btn-bg,var(--blue-500));color:var(--mt-email-cta-btn-fg,var(--gray-white));font-size:15px;font-weight:600;border-radius:8px;text-decoration:none;">Call to action</a></p>`,
  /** Second solid CTA color; Page Settings → Button 2 fill (Elements → Button 2). */
  buttonAlt: `<p style="text-align:center;margin:12px 0;"><a class="e-rte-email-btn e-rte-email-btn--alt" data-mt-cta-slot="2" href="https://example.com" style="display:inline-block;min-width:200px;box-sizing:border-box;text-align:center;padding:12px 28px;background:var(--mt-email-cta-btn-bg-alt,var(--indigo-500));color:var(--mt-email-cta-btn-alt-fg,var(--gray-white));font-size:15px;font-weight:600;border-radius:8px;text-decoration:none;">Call to action</a></p>`,
  image: `<p style="margin:10px 0;text-align:center;"><span class="e-rte-email-image-placeholder" style="display:block;min-height:64px;background:#f3f4f6;border:1px dashed #cbd5e1;border-radius:6px;line-height:64px;font-size:inherit;color:#64748b;">Image</span></p>`,
  /** Image-only slot: no text (logos are uploaded images). `contenteditable="false"` blocks typing in the chip. */
  logo: `<p style="margin:10px 0;text-align:center;"><span class="e-rte-email-logo-placeholder" contenteditable="false" role="button" tabindex="0" title="Double-click to upload a logo image" aria-label="Logo placeholder. Double-click to choose an image file." style="display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;min-width:48px;min-height:48px;width:48px;height:48px;background:#e0e7ff;border:1px dashed #93c5fd;border-radius:8px;cursor:pointer;user-select:none;-webkit-user-select:none;"></span></p>`,
  divider: `<hr class="e-rte-email-divider" style="border:0;border-top:1px solid #cbd5e1;margin:14px 0;opacity:1;" />`,
  /** Full-width title strip; color from Page Settings → Header band color when present. */
  headerBand: `<div class="mt-email-tpl__email-header-band" data-email-header-band="true"><p style="margin:0;font-size:inherit;font-weight:800;letter-spacing:0.04em;color:var(--blue-700);">Section headline</p></div>`,
  /**
   * Icon + title + description row (same markup/classes as the predefined
   * “Promotion & offers” email mock). Styled under `.mt-marketing-email-tpl-root`.
   * Click the icon in the editor to swap SVG (see `FEATURE_ROW_ICON_CHOICES`).
   */
  featureRow: `<div class="mt-email-tpl__promo-row"><div class="mt-email-tpl__promo-icon e-rte-email-promo-icon" contenteditable="false" tabindex="0" role="button" aria-label="Change icon" title="Click to change icon">${FEATURE_ROW_DEFAULT_SVG}</div><div class="mt-email-tpl__promo-copy"><p class="mt-email-tpl__promo-item-title">Feature title</p><p class="mt-email-tpl__promo-item-desc">Short supporting line—edit to describe an offer, reminder, or service highlight.</p></div></div>`,
  columns3: wrapEmailBlockInRow(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border-spacing:0;border:0;"><tr><td valign="top" width="33.33%" style="padding:0 6px 0 0;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Column 1</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">First column copy.</p></td><td valign="top" width="33.33%" style="padding:0 6px;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Column 2</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Second column copy.</p></td><td valign="top" width="33.33%" style="padding:0 0 0 6px;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Column 3</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Third column copy.</p></td></tr></table>`,
  ),
  columns2575: wrapEmailBlockInRow(
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border-spacing:0;border:0;"><tr><td valign="top" width="25%" style="padding:0 10px 0 0;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Sidebar</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Short nav or highlights.</p></td><td valign="top" width="75%" style="padding:0 0 0 10px;border:0;"><p style="margin:0 0 8px;font-size:inherit;font-weight:600;color:#1e293b;">Main content</p><p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">Primary message and calls to action.</p></td></tr></table>`,
  ),
  socialRow: wrapEmailBlockInRow(
    `<table role="presentation" class="e-rte-email-social" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border-spacing:0;border:0;"><tr><td align="center" style="padding:8px 0 10px;border:0;"><a href="#" style="display:inline-block;margin:0 10px;font-size:inherit;font-weight:600;color:#475569;text-decoration:none;">Facebook</a><span style="color:#cbd5e1;">|</span><a href="#" style="display:inline-block;margin:0 10px;font-size:inherit;font-weight:600;color:#475569;text-decoration:none;">Instagram</a><span style="color:#cbd5e1;">|</span><a href="#" style="display:inline-block;margin:0 10px;font-size:inherit;font-weight:600;color:#475569;text-decoration:none;">Website</a></td></tr></table>`,
  ),
  footerBlock: wrapEmailBlockInRow(
    `<div class="e-rte-email-footer-block" style="max-width:100%;margin:16px 0 0;padding:16px 20px 20px;border-top:1px solid var(--gray-200);background:var(--gray-100);font-size:12px;line-height:1.55;color:var(--gray-500);text-align:center;"><p style="margin:0 0 8px;">Your practice name · Street address · City, ST ZIP</p><p style="margin:0;"><a href="#" style="color:var(--blue-500);text-decoration:underline;">Unsubscribe</a><span style="color:var(--gray-400);"> · </span><a href="#" style="color:var(--blue-500);text-decoration:underline;">Email preferences</a></p></div>`,
  ),
  spacer: wrapEmailBlockInRow(
    `<div class="e-rte-email-spacer" data-email-spacer="true" style="max-width:100%;height:28px;min-height:28px;line-height:28px;font-size:1px;" aria-hidden="true">&#8203;</div>`,
  ),
  quote: wrapEmailBlockInRow(
    `<blockquote class="e-rte-email-quote" style="margin:14px 0;padding:12px 16px 12px 14px;border-left:4px solid var(--mt-email-cta-btn-bg,#3b82f6);background:#f8fafc;border-radius:0 8px 8px 0;"><p style="margin:0 0 6px;font-size:inherit;font-style:italic;line-height:1.55;color:#334155;">&ldquo;Add a short testimonial or quote here.&rdquo;</p><p style="margin:0;font-size:inherit;font-weight:600;color:#1e293b;">&mdash; Name, role or location</p></blockquote>`,
  ),
  tableInfo: wrapEmailBlockInRow(
    `<table role="presentation" class="e-rte-email-info-table" width="100%" cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;border-spacing:0;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;"><thead><tr><th colspan="2" style="padding:10px 12px;background:#f1f5f9;font-size:inherit;font-weight:600;color:#0f172a;text-align:left;border-bottom:1px solid #e2e8f0;">Office hours</th></tr></thead><tbody><tr><td style="padding:10px 12px;font-size:inherit;color:#334155;border-bottom:1px solid #f1f5f9;width:40%;">Monday &ndash; Thursday</td><td style="padding:10px 12px;font-size:inherit;color:#475569;border-bottom:1px solid #f1f5f9;">8:00 AM &ndash; 5:00 PM</td></tr><tr><td style="padding:10px 12px;font-size:inherit;color:#334155;border-bottom:1px solid #f1f5f9;">Friday</td><td style="padding:10px 12px;font-size:inherit;color:#475569;border-bottom:1px solid #f1f5f9;">8:00 AM &ndash; 2:00 PM</td></tr><tr><td style="padding:10px 12px;font-size:inherit;color:#334155;">Phone</td><td style="padding:10px 12px;font-size:inherit;color:#475569;">(555) 123-4567</td></tr></tbody></table>`,
  ),
  heroImage: wrapEmailBlockInRow(
    `<div class="e-rte-email-hero-image" style="max-width:100%;border-radius:10px;overflow:hidden;border:1px solid #e2e8f0;"><div class="e-rte-email-hero-image-placeholder" contenteditable="false" tabindex="0" role="button" aria-label="Add hero image" title="Click to add or change image" style="min-height:140px;background:linear-gradient(180deg,#cbd5e1,#e2e8f0);display:flex;align-items:center;justify-content:center;color:#64748b;font-size:inherit;text-align:center;padding:16px;cursor:pointer;user-select:none;">Click to add hero image</div><div class="e-rte-email-hero-copy-band" style="padding:16px 18px;background:var(--mt-email-hero-copy-band-bg,#0f172a);"><p style="margin:0;font-size:inherit;font-weight:700;color:#fff;">Headline below the image</p><p style="margin:8px 0 0;font-size:inherit;line-height:1.45;color:#cbd5e1;">Supporting line for your announcement.</p></div></div>`,
  ),
  textLinkCta: wrapEmailBlockInRow(
    `<p style="text-align:center;margin:12px 0;"><a class="e-rte-email-text-link-cta" data-mt-text-link-slot="1" href="https://example.com" style="font-size:inherit;font-weight:600;color:var(--mt-email-cta-btn-bg,#3b82f6);text-decoration:underline;">Text link call to action &rarr;</a></p>`,
  ),
  buttonOutline: `<p style="text-align:center;margin:12px 0;"><a class="e-rte-email-btn e-rte-email-btn--outline" data-mt-cta-slot="1" href="https://example.com" style="display:inline-block;min-width:200px;box-sizing:border-box;text-align:center;padding:12px 28px;background:transparent;color:var(--mt-email-cta-btn-bg,var(--blue-500));font-size:15px;font-weight:600;border-radius:8px;border:2px solid var(--mt-email-cta-btn-bg,var(--blue-500));text-decoration:none;">Outlined button</a></p>`,
} as const

/**
 * Full email starters: composed from {@link wrapEmailBlockInRow} and
 * {@link TEMPLATE_EMAIL_SNIPPETS} so structure matches Elements (rows, logo,
 * divider, button) for consistent editing.
 */
export const TEMPLATE_EMAIL_STARTER_LAYOUTS: IEmailStarterLayout[] = [
  {
    id: 'letter',
    title: 'Letter',
    subtitle: 'Headline and body copy',
    html: [
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;font-weight:700;color:#1e293b;">Headline</p>`,
      ),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;line-height:1.55;color:#334155;">Write your message here. Use Elements to add buttons, images, and sections.</p>`,
      ),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;line-height:1.55;color:#334155;">Best regards,<br/>Your practice</p>`,
      ),
    ].join(''),
  },
  {
    id: 'hero',
    title: 'Hero + text',
    subtitle: 'Banner and main content',
    html: [
      wrapEmailBlockInRow(
        `<div class="mt-email-tpl__email-hero-starter" style="background:var(--mt-email-header-band-bg,linear-gradient(135deg,#3b82f6,#1d4ed8));padding:20px 16px;border-radius:0px;text-align:center;"><p style="margin:0;font-size:inherit;font-weight:700;color:#fff;">Welcome</p><p style="margin:8px 0 0;font-size:inherit;font-weight:400;color:#e0e7ff;">Supporting line for your announcement</p></div>`,
      ),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;line-height:1.55;color:#334155;">Your main email body starts here.</p>`,
      ),
    ].join(''),
  },
  {
    id: 'newsletter',
    title: 'Newsletter',
    subtitle: 'Title and body',
    html: [
      wrapEmailBlockInRow(TEMPLATE_EMAIL_SNIPPETS.logo),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;font-weight:700;color:#0f172a;text-align:center;">Practice update</p><p style="margin:6px 0 0;font-size:inherit;font-weight:500;color:#64748b;text-align:center;">Optional date or subtitle</p>`,
      ),
      wrapEmailBlockInRow(TEMPLATE_EMAIL_SNIPPETS.divider),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;line-height:1.55;color:#334155;">Your opening paragraph. Add more blocks from Elements anytime.</p>`,
      ),
    ].join(''),
  },
  {
    id: 'cta',
    title: 'Announcement',
    subtitle: 'Short copy + CTA',
    html: [
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;font-weight:600;color:#1e293b;">Quick update</p>`,
      ),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;line-height:1.5;color:#475569;">We wanted to share something important with you.</p>`,
      ),
      wrapEmailBlockInRow(
        TEMPLATE_EMAIL_SNIPPETS.button.replace(
          'Call to action',
          'Primary action',
        ),
      ),
      wrapEmailBlockInRow(
        `<p style="margin:0;font-size:inherit;color:#64748b;text-align:center;">Questions? Reply to this email.</p>`,
      ),
    ].join(''),
  },
]

/** Email Elements: structure → columns → media → content → footer/social → CTAs → utilities. */
export const TEMPLATE_EMAIL_ELEMENT_BUTTONS: IElementButton[] = [
  { id: 'layout', text: 'Layout', icon: 'mgc_layout_grid_line' },
  { id: 'text', text: 'Text', icon: 'mgc_text_line' },
  { id: 'spacer', text: 'Spacer', icon: 'mgc_space_line' },
  { id: 'divider', text: 'Divider', icon: 'mgc_dividing_line_line' },
  {
    id: 'headerBand',
    text: 'Header band',
    icon: 'mgc_rectangle_line',
  },
  { id: 'columns', text: '2 columns', icon: 'mgc_columns_2_line' },
  { id: 'columns3', text: '3 columns', icon: 'mgc_table_3_line' },
  { id: 'columns2575', text: 'Sidebar', icon: 'mgc_layout_3_line' },
  { id: 'image', text: 'Image', icon: 'mgc_pic_fill' },
  {
    id: 'logo',
    text: 'Logo',
    icon: 'mgc_font_line',
    hint: 'Image-only: double-click the chip to upload a logo. Pre-template circles: click to upload.',
  },
  {
    id: 'heroImage',
    text: 'Hero block',
    icon: 'mgc_layout_4_line',
    hint: 'Click the top to add an image. Page Settings → Hero text band color for the headline strip.',
  },
  {
    id: 'featureRow',
    text: 'Feature row',
    icon: 'mgc_star_line',
  },
  { id: 'quote', text: 'Quote', icon: 'mgc_quote_left_line' },
  {
    id: 'tableInfo',
    text: 'Info table',
    icon: 'mgc_table_line',
    hint: 'Click inside an existing info table, then click this again to add a row.',
  },
  { id: 'socialRow', text: 'Social row', icon: 'mgc_share_forward_line' },
  { id: 'footerBlock', text: 'Footer', icon: 'mgc_information_line' },
  {
    id: 'button',
    text: 'Solid button',
    icon: 'mgc_add_square_line',
    hint: 'Anchor styled as a solid button—edit href (starts as example.com).',
  },
  {
    id: 'buttonOutline',
    text: 'Outline button',
    icon: 'mgc_square_line',
    hint: 'Anchor with outline style—edit href (starts as example.com).',
  },
  {
    id: 'textLinkCta',
    text: 'Text link',
    icon: 'mgc_anchor_line',
    hint: 'Text anchor with href—edit the URL in HTML.',
  },
  { id: 'hyperlink', text: 'Hyperlink', icon: 'mgc_link_2_line' },
  { id: 'variable', text: 'Variables', icon: 'ti ti-variable' },
]

/** SMS template editor */
export const TEMPLATE_SMS_ELEMENT_BUTTONS: IElementButton[] = [
  { id: 'text', text: 'Text', icon: 'mgc_text_line' },
  { id: 'hyperlink', text: 'Hyperlink', icon: 'mgc_link_2_line' },
  { id: 'variable', text: 'Variables', icon: 'ti ti-variable' },
  { id: 'emoji', text: 'Emoji', icon: 'mgc_emoji_fill' },
]

export const HYPERLINK_HTML: string = `<a id=":::::SET_ID:::::" class="e-rte-anchor danger" href=":::::SET_URL:::::" title=":::::SET_URL:::::" target="_blank" aria-label="Open in new window" contenteditable="false">(Link)</a>`

export const PLACEHOLDER_URL = ':::::SET_URL:::::'
export const URL_REGEX =
  /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/
export const CSS_CLASSES = {
  ANCHOR: 'e-rte-anchor',
  DANGER: 'danger',
} as const

export const PALETTE = [
  'gray',
  'red',
  'orange',
  'yellow',
  'pale-green',
  'vivid-green',
  'teal',
  'blue',
  'indigo',
  'violet',
]

export const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

export const PAGE_CAPACITY = 95
export const HEAD_HEIGHT = 60
export const CHILD_HEIGHT = 24

export const VARIABLE_LIST = [
  {
    name: 'Appointment date',
    text: '(appointment_date)',
    value: 'appointment_date',
    sample: 'August 21, 2025',
    type: 'variable',
  },
  {
    name: 'Patient name',
    text: '(patient_name)',
    value: 'patient_name',
    sample: 'John Doe',
    type: 'variable',
  },
  {
    name: 'Patient number',
    text: '(patient_number)',
    value: 'patient_number',
    sample: '+1 (234) 567-8900',
    type: 'variable',
  },
  {
    name: 'Practice name',
    text: '(practice_name)',
    value: 'practice_name',
    sample: 'Clinic XYZ',
    type: 'variable',
  },
  {
    name: 'Practice number',
    text: '(practice_number)',
    value: 'practice_number',
    sample: '+1 (098) 765-4321',
    type: 'variable',
  },
  {
    name: 'Preferred name',
    text: '(preferred_name)',
    value: 'preferred_name',
    sample: 'John',
    type: 'variable',
  },
  {
    name: 'Promo date',
    text: '(promo_date)',
    value: 'promo_date',
    sample: 'June 3rd, 2025 - June 7th, 2025',
    type: 'variable',
  },
  {
    name: 'Practice services',
    text: '(practice_services)',
    value: 'practice_services',
    sample:
      '-General Dentistry\n-Endodonics\n-Orthodontics\n-Dental Radiography\n-Oral Surgery',
    type: 'value',
  },
]

export const RECIPIENT_TYPE_OPTIONS = [
  { label: 'Location', value: 'Location' },
  { label: 'Patient status', value: 'Patient status' },
  { label: 'Patient type', value: 'Patient type' },
  { label: 'Appointment status', value: 'Appointment status' },
]

export const AGE_RANGE_OPTIONS = [
  { label: '0-12 (Children)', value: '0-12' },
  { label: '13-17 (Teenagers)', value: '13-17' },
  { label: '18-24 (Young Adults)', value: '18-24' },
  { label: '25-34 (Adults)', value: '25-34' },
  { label: '35-49 (Middle-Aged Adults)', value: '35-49' },
  { label: '50-64 (Middle-Aged Adults)', value: '50-64' },
  { label: '65+ (Senior Citizens)', value: '65+' },
]

export const CELEBRATION_OPTIONS = [
  { id: 'Birthday', text: 'Birthday' },
  { id: 'Anniversary', text: 'Anniversary' },
  { id: 'Membership', text: 'Membership' },
]

export const OPT_OUT_KEYWORDS = ['STOP', 'STOPALL']
